import React from 'react';
import { Button } from '@/components/ui/button';

/**
 * Quote chooser modal — opens when staff/installer clicks "Quote Customer"
 * on a lead that already has a quote on file. Lets them either resume editing
 * the existing quote (or create a new revision if it was already sent) or
 * start a brand-new fresh quote.
 *
 * Pure presentation — all state stays in the parent (ManageLeads).
 */
export default function QuoteChooserModal({ lead, onClose, onEditOrRevise, onFresh }) {
  if (!lead) return null;
  const isDraft = lead.latest_quote_status === 'draft';
  const nextRevision = (lead.latest_quote_revision || 1) + 1;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Existing Quote</h2>
          <p className="text-xs text-gray-500 mt-1">
            <strong>{lead.customer_name}</strong> already has a quote on file —
            <span className="font-mono ml-1">{lead.latest_quote_reference}</span>
            {lead.latest_quote_revision > 1 && ` (v${lead.latest_quote_revision})`}
            {' · '}{lead.latest_quote_status || 'draft'}
            {' · '}{lead.latest_quote_m2}m² · £{(lead.latest_quote_total || 0).toLocaleString('en-GB')}
          </p>
        </div>
        <div className="p-5 space-y-2">
          <button
            onClick={onEditOrRevise}
            className="w-full text-left p-4 rounded-xl border-2 border-[#006400] bg-[#006400]/5 hover:bg-[#006400]/10 transition-colors"
            data-testid="quote-chooser-edit"
          >
            <div className="font-semibold text-[#006400] text-sm">
              {isDraft ? '✏️ Edit existing draft' : '🔁 Create a new revision'}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {isDraft
                ? 'Continue editing the same draft — changes save in place.'
                : `Pre-fills from the sent quote. Saving creates a new revision (v${nextRevision}).`}
            </div>
          </button>
          <button
            onClick={onFresh}
            className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            data-testid="quote-chooser-fresh"
          >
            <div className="font-semibold text-gray-900 text-sm">🆕 Start a fresh quote</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Build a brand-new quote from scratch. The existing quote stays in the history.
            </div>
          </button>
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="quote-chooser-cancel">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
